import {
  ArrowSquareOut,
  ArrowsLeftRight,
  FolderOpen,
  FolderPlus,
  Lock,
  PaperPlaneTilt,
  Robot,
  Sparkle,
  Trash,
  TreeStructure,
  UserPlus,
  X,
} from "@phosphor-icons/react";

function RetrievalResults({ results, status, onBrowse }) {
  if (!Array.isArray(results) || results.length === 0) {
    if (!status?.partial && !status?.truncated) return null;
  }
  return (
    <div className="w-full space-y-1.5" data-testid="file-retrieval-results">
      {(status?.partial || status?.truncated) && (
        <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[10px] leading-relaxed text-amber-800" role="status">
          {status.partial ? "Some folders could not be checked. " : ""}
          {status.truncated ? "Showing a partial result because the search limit was reached." : "Results may be incomplete."}
        </p>
      )}
      {results.map((result, index) => (
        <div key={`${result.relative_path || result.name}-${index}`} className="rounded-lg border border-slate-200 bg-white p-2.5 shadow-sm">
          <div className="flex items-start gap-1.5">
            {result.kind === "folder" ? <FolderOpen size={13} className="mt-0.5 flex-shrink-0 text-[#0078D4]" /> : <ArrowSquareOut size={13} className="mt-0.5 flex-shrink-0 text-violet-500" />}
            <div className="min-w-0 flex-1">
              <p className="truncate text-[11px] font-semibold text-slate-700">{result.name}</p>
              {result.relative_path && <p className="mt-0.5 break-words text-[10px] leading-relaxed text-slate-500">{result.relative_path}</p>}
              {result.protected && <p className="mt-1 flex items-center gap-1 text-[10px] text-slate-500"><Lock size={10} /> Protected · read only</p>}
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {result.web_url && (
              <a
                href={result.web_url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded bg-violet-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-400"
                aria-label={`Open ${result.name} in OneDrive`}
              >
                Open in OneDrive
              </a>
            )}
            {result.kind === "folder" && result.navigation_target && (
              <button
                type="button"
                onClick={() => onBrowse(result.navigation_target)}
                className="rounded border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-400"
                aria-label={`Browse ${result.name} in Explore Files`}
              >
                Browse here
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ChatMessage({ msg, onDelete, onMove, onCreate, onAddContractor, onModifyLayout, onBrowse }) {
  const isUser = msg.role === "user";
  const hasActions = Array.isArray(msg.actions) && msg.actions.length > 0;

  return (
    <div className={`flex gap-2 ${isUser ? "flex-row-reverse" : "flex-row"}`} data-testid={`chat-msg-${msg.id}`}>
      {!isUser && (
        <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Robot size={12} className="text-violet-600" />
        </div>
      )}

      <div className={`flex flex-col gap-1.5 max-w-[85%] ${isUser ? "items-end" : "items-start"}`}>
        <div className={`px-3 py-2 rounded-lg text-xs leading-relaxed ${isUser ? "bg-violet-600 text-white" : msg.isError ? "bg-red-50 border border-red-200 text-red-700" : "bg-slate-100 text-slate-800"}`}>
          {msg.content.split("**").map((part, index) => index % 2 === 1 ? <strong key={index}>{part}</strong> : part)}
        </div>

        {!isUser && <RetrievalResults results={msg.results} status={msg.retrievalStatus} onBrowse={onBrowse} />}

        {hasActions && msg.actions.map((action, index) => (
          <div key={index} className="w-full bg-white border border-slate-200 rounded-lg p-2.5 shadow-sm">
            {action.type === "delete" ? (
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Trash size={11} className="text-red-500 flex-shrink-0" />
                  <span className="text-[11px] font-semibold text-slate-700 truncate">Delete: {action.item_name}</span>
                </div>
                {action.reason && <p className="text-[10px] text-slate-500 mb-2 leading-relaxed">{action.reason}</p>}
                <button onClick={() => onDelete(action)} className="text-[11px] font-semibold text-white bg-red-600 hover:bg-red-700 px-2.5 py-1 rounded transition-colors" data-testid={`action-delete-btn-${action.item_name}`}>
                  Review &amp; Delete
                </button>
              </div>
            ) : action.type === "move" ? (
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <ArrowsLeftRight size={11} className="text-blue-500 flex-shrink-0" />
                  <span className="text-[11px] font-semibold text-slate-700 truncate">Move: {action.item_name}</span>
                </div>
                {action.reason && <p className="text-[10px] text-slate-500 mb-2 leading-relaxed">{action.reason}</p>}
                {action.destination_folder_id && <p className="text-[10px] text-slate-600 mb-2 font-medium">→ {action.destination_folder_name || "Selected folder"}</p>}
                <button onClick={() => onMove(action)} className="text-[11px] font-semibold text-white bg-blue-600 hover:bg-blue-700 px-2.5 py-1 rounded transition-colors" data-testid={`action-move-btn-${action.item_name}`}>
                  {action.destination_folder_id ? "Approve & Move" : "Pick Destination"}
                </button>
              </div>
            ) : action.type === "create_folder" ? (
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <FolderPlus size={11} className="text-amber-600 flex-shrink-0" />
                  <span className="text-[11px] font-semibold text-slate-700 truncate">Create Folder: {action.folder_name}</span>
                </div>
                {action.reason && <p className="text-[10px] text-slate-500 mb-2 leading-relaxed">{action.reason}</p>}
                <button onClick={() => onCreate(action)} className="text-[11px] font-semibold text-white bg-amber-600 hover:bg-amber-700 px-2.5 py-1 rounded transition-colors" data-testid={`action-create-folder-btn-${action.folder_name}`}>
                  Approve &amp; Create
                </button>
              </div>
            ) : action.type === "add_contractor" ? (
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <UserPlus size={11} className="text-emerald-600 flex-shrink-0" />
                  <span className="text-[11px] font-semibold text-slate-700 truncate">Add Contractor: {action.contractor_name}</span>
                </div>
                {action.reason && <p className="text-[10px] text-slate-500 mb-2 leading-relaxed">{action.reason}</p>}
                <button onClick={() => onAddContractor(action)} className="text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1 rounded transition-colors" data-testid={`action-add-contractor-btn-${action.contractor_name}`}>
                  Approve &amp; Add Contractor
                </button>
              </div>
            ) : action.type === "modify_layout" ? (
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <TreeStructure size={11} className="text-violet-600 flex-shrink-0" />
                  <span className="text-[11px] font-semibold text-slate-700">Modify Folder Layout</span>
                </div>
                <p className="text-[10px] text-slate-700 mb-1.5 font-medium">{action.modification}</p>
                {action.changes && action.changes.length > 0 && (
                  <ul className="text-[10px] text-slate-500 mb-2 space-y-0.5 list-disc list-inside">
                    {action.changes.map((change, ci) => (
                      <li key={ci}>
                        {change.action === "add_folder" && `Add folder: ${change.folder?.name || "unnamed"}`}
                        {change.action === "remove_folder" && `Remove: ${change.folder_name}`}
                        {change.action === "change_purpose" && `Update purpose: ${change.path}`}
                        {change.action === "change_protection" && `Change protection: ${change.path} → ${change.new_protection}`}
                        {change.action === "set_drawings_location" && `Set drawings location: ${change.path}`}
                      </li>
                    ))}
                  </ul>
                )}
                {action.reason && <p className="text-[10px] text-slate-500 mb-2 leading-relaxed italic">{action.reason}</p>}
                <button onClick={() => onModifyLayout(action)} className="text-[11px] font-semibold text-white bg-violet-600 hover:bg-violet-700 px-2.5 py-1 rounded transition-colors" data-testid="action-modify-layout-btn">
                  Approve &amp; Apply
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export function FileAssistantSidebar({
  chatOpen,
  setChatOpen,
  chatMessages,
  chatLoading,
  chatInput,
  setChatInput,
  handleSendChat,
  handleClearChat,
  chatEndRef,
  onDelete,
  onMove,
  onCreate,
  onAddContractor,
  onModifyLayout,
  onBrowse,
}) {
  return (
    <div className={`flex flex-col bg-white border-l border-slate-200 flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden ${chatOpen ? "w-96" : "w-0 border-l-0"}`} data-testid="ai-chat-sidebar">
      {chatOpen && (
        <>
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-violet-600 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Robot size={16} className="text-white" />
              <span className="text-sm font-semibold text-white">AI File Assistant</span>
            </div>
            <div className="flex items-center gap-1">
              {chatMessages.length > 0 && (
                <button onClick={handleClearChat} title="Clear conversation" className="text-violet-200 hover:text-white text-[10px] px-2 py-1 hover:bg-white/10 rounded transition-colors" data-testid="chat-clear-btn">
                  Clear
                </button>
              )}
              <button onClick={() => setChatOpen(false)} className="text-violet-200 hover:text-white p-1 rounded hover:bg-white/10 transition-colors" data-testid="chat-close-btn">
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
            {chatMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-8 gap-3">
                <div className="w-12 h-12 rounded-full bg-violet-50 border border-violet-100 flex items-center justify-center">
                  <Sparkle size={22} className="text-violet-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-1">AI File Assistant</p>
                  <p className="text-xs text-slate-400 leading-relaxed max-w-[220px]">
                    Ask me to find files, create folders, add contractors, or clean up your project.
                  </p>
                </div>
                <div className="mt-2 flex flex-col gap-1.5 w-full">
                  {["Find all PDF drawings", "Create a folder called Meeting Notes", "Add a Mechanical contractor", "Which files are largest?"].map((suggestion) => (
                    <button key={suggestion} onClick={() => setChatInput(suggestion)} className="text-xs text-left px-3 py-2 bg-slate-50 hover:bg-violet-50 border border-slate-200 hover:border-violet-200 rounded text-slate-600 hover:text-violet-700 transition-colors" data-testid={`chat-suggestion-${suggestion}`}>
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              chatMessages.map((msg) => (
                <ChatMessage key={msg.id} msg={msg} onDelete={onDelete} onMove={onMove} onCreate={onCreate} onAddContractor={onAddContractor} onModifyLayout={onModifyLayout} onBrowse={onBrowse} />
              ))
            )}
            {chatLoading && (
              <div className="flex items-center gap-2 py-1" data-testid="chat-loading" role="status" aria-live="polite">
                <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <Robot size={12} className="text-violet-500" />
                </div>
                <div className="flex gap-1 items-center">
                  <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
                <span className="text-[10px] text-slate-500">Checking this folder and its subfolders…</span>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="flex-shrink-0 px-4 py-3 border-t border-slate-100 bg-slate-50">
            <div className="flex items-end gap-2">
              <textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendChat();
                  }
                }}
                placeholder="Ask about your files…"
                rows={2}
                className="flex-1 resize-none text-sm px-3 py-2 border border-slate-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400 placeholder-slate-400"
                data-testid="chat-input"
                disabled={chatLoading}
              />
              <button onClick={handleSendChat} disabled={!chatInput.trim() || chatLoading} className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded transition-colors" data-testid="chat-send-btn">
                <PaperPlaneTilt size={15} />
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5">Enter to send · Shift+Enter for new line</p>
          </div>
        </>
      )}
    </div>
  );
}
